import { useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useGoalDetailQuery } from "web_myplann/Api/Goals/useGoalDetailQuery";
import { useGoalForm } from "web_myplann/Goals/Forms/useGoalForm";
import { deriveGoalAmounts } from "web_myplann/Goals/Helpers/goalAmounts";
import { useGoalCategoryFieldMemory } from "web_myplann/Goals/Hooks/useGoalCategoryFieldMemory";
import { useSuggestedGoalName } from "web_myplann/Goals/Hooks/useSuggestedGoalName";
import { mapGoalDtoToForm } from "web_myplann/Goals/Mappers/mapGoalDtoToForm";
import { mapParamsToGoalForm } from "web_myplann/Goals/Mappers/mapParamsToGoalForm";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import { getRoute } from "web_myplann/Infrastructure/Utils/UrlUtils";
import { getTypedParam } from "web_myplann/Shared/Utils/getTypedParam";

export const useGoalFormPage = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const routeParams = useParams<{ id: string }>();

	const goalId = getTypedParam(routeParams, "id", Number) ?? null;
	const isEdit = goalId !== null;

	const goToList = () => navigate(getRoute(Routing.Goals.GoalsList));

	const { data: goalDto } = useGoalDetailQuery(goalId, {
		enabled: isEdit,
	});

	const initialValues = useMemo(
		() =>
			isEdit
				? goalDto
					? mapGoalDtoToForm(goalDto)
					: undefined
				: mapParamsToGoalForm(searchParams),
		[isEdit, goalDto, searchParams],
	);

	const form = useGoalForm({
		goalId: goalId ?? undefined,
		initialValues,
		onSuccess: (resultId) => {
			if (isEdit) {
				navigate(-1);
				return;
			}
			if (resultId == null) {
				goToList();
				return;
			}
			navigate(Routing.Goals.GoalDetail.path(String(resultId)), {
				state: { welcomePin: true, ownFunds: form.getValues("ownFunds") ?? 0 },
			});
		},
	});

	const { control, submit, isSubmitting, watch } = form;
	const { selectCategory } = useGoalCategoryFieldMemory(form);

	const category = watch("category");
	const dueDate = watch("dueDate");
	const totalValue = watch("totalValue") ?? 0;
	const trimmedNameLength = (watch("name") ?? "").trim().length;
	const nameValid = trimmedNameLength >= 1 && trimmedNameLength <= 100;

	const { savingsTarget, realizationMode, hasMonthlyHero } = deriveGoalAmounts({
		totalValue,
		ownFunds: watch("ownFunds") ?? 0,
		loanAmount: watch("loanAmount") ?? 0,
		dueDate,
	});

	const { sectionTitle } = useSuggestedGoalName(form, category);

	const minDueDate = useMemo(() => new Date(), []);

	return {
		control,
		submit,
		isSubmitting,
		isEdit,
		category,
		selectCategory,
		dueDate,
		savingsTarget,
		realizationMode,
		hasMonthlyHero,
		submitDisabled: totalValue <= 0 || !nameValid || isSubmitting,
		sectionTitle,
		minDueDate,
		goToList,
	};
};
